import { MultiSelectionCell } from '../../cells/body/selector/multi/multi.component';
import { SingleSelectionCell } from '../../cells/body/selector/single/single.component';
import { TextHeaderCellComponent } from '../../cells/header/text/text.component';
import { SelectorAllHeaderCellComponent } from '../../cells/header/selector/all/selector-all.component';
import { Col } from './col.class';
import { ColReseter } from './reseter.class';
import { ColsCasher } from './cash.class';
import { AbstractTable, ColsArgs, ColsMap, ICol } from '../context/abstract/table.class';
import { STUB_FN_ZERO } from 'projects/exi-ui/src/lib/utils/stubs';
import { SelectorSingleComponent } from '../../cells/header/selector/single/selector-single.component';



export class Cols<D extends object> {
   public visible: Col<D>[];
   public draggable: Col<D>[];
   public pinned: Col<D>[];
   public lastPinned: Col<D>;
   readonly all: Col<D>[];
   readonly controlable: Col<D>[];
   readonly sortable: Col<D>[];
   readonly templates: ICol<D>[];
   readonly isDragable: boolean;
   readonly isSizable: boolean;
   readonly reseter: ColReseter<D>;
   readonly cash: ColsCasher;

   constructor(
      private ctx: AbstractTable<D>,
      { templates, isDragable, isSizable }: ColsArgs<D>,
   ) {
      this.isDragable = isDragable === undefined || isDragable;
      this.isSizable = isSizable === undefined || isSizable;
      this.templates = this.processTemplates(templates);
      this.all = this.getDefaultCols();
      this.controlable = this.all.filter(col => col.isControlable);
      this.sortable = this.all.filter(col => col.sortable.isActive);
      this.reseter = new ColReseter(ctx, this);
      this.cash = new ColsCasher(this.all, this.reseter, ctx);
      this.cash.apply();
   }


   private processTemplates(colsMap: ColsMap<D>): ICol<D>[] {
      const templates: ICol<D>[] = Object.entries(colsMap)
         .map(([name, template]) => ({
            title: () => name,
            id: name,
            ...template,
         }));

      switch (this.ctx.selection.mode) {
         case 'single':
            templates.unshift({
               id: `__SingleSelect__`,
               title: () => 'Selection',
               header: SelectorSingleComponent,
               minWidth: 40,
               isPinned: true,
               isControlable: false,
               cell: new SingleSelectionCell(this.ctx.selection)
            });
            break;
         case 'multi':
            templates.unshift({
               id: `__MultiSelect__`,
               title: () => 'Selection',
               header: SelectorAllHeaderCellComponent,
               minWidth: 40,
               isPinned: true,
               cell: new MultiSelectionCell(this.ctx.selection)
            });
            break;
      }

      for (const iCol of templates) {
         iCol.header = iCol.header || TextHeaderCellComponent;
         iCol.isControlable = iCol.isControlable === undefined || iCol.isControlable;
         iCol.isVisible = iCol.isVisible === undefined || iCol.isVisible;
         iCol.isPinned = !!iCol.isPinned;

      }

      return templates;
   }


   private getDefaultCols(): Col<D>[] {
      const { templates, ctx } = this;
      const totalWidthPx = templates
         .filter(tp => tp.isPinned === false)
         .filter(tp => tp.isVisible)
         .reduce((acc, tp) => acc + tp.minWidth, 0);

      const cols = templates.map((iCol, i) => new Col(iCol, {
         order: i,
         ctx,
         width: {
            initialMinWidth: iCol.minWidth,
            "min-width.px": iCol.minWidth,
            "width.%": iCol.isPinned || !iCol.isVisible ? null : iCol.minWidth * 100 / totalWidthPx,
            "max-width.px": iCol.isPinned ? iCol.minWidth : null,
         },
         sortable: {
            id: iCol.sorterMark || iCol.title(),
            defaults: 'none',
            getter: iCol.cell.stringify,
            isActive: !!iCol.sorter || !!iCol.sorterMark,
            sorter: iCol.sorter || STUB_FN_ZERO,
         }
      }));

      return cols;
   }


   private recalcColsWidth() {
      const otherCols = this.visible.filter($col => $col.isPinned === false);
      const totalWidthPx = this.visible
         .filter(col => col.isPinned === false)
         .reduce((acc, col) => acc + col.width["min-width.px"], 0);
   
      for (const col of otherCols) {
         col.width['width.%'] = col.width["min-width.px"] * 100 / totalWidthPx;
      }
   }


   public toggleVisibility(col: Col<D>) {
      col.isVisible = !col.isVisible;
      this.reseter.organize();
      this.recalcColsWidth();
   }


   public togglePinned(col: Col<D>) {
      col.isPinned = !col.isPinned;

      if (col.isPinned) {
         col.width["width.%"] = null;
         col.width["min-width.px"] = col.defaults["min-width.px"];
         col.width["max-width.px"] = col.width.initialMinWidth;
      } else {
         col.width["max-width.px"] = null;
      }

      this.draggable.forEach(col_ => col_.order += 1);
      col.order = this.pinned[this.pinned.length - 1].order + 1;

      this.reseter.organize();
      this.recalcColsWidth();
   }
}



